import { useState } from 'react';

export function Hero() {
  const [activeTab, setActiveTab] = useState<'overview' | 'apps' | 'notices' | 'certs'>('overview');

  return (
    <section className="relative pt-36 pb-28 px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
        {/* Left Column — Text & CTAs */}
        <div>
          <div className="inline-flex items-center gap-2 text-[12px] font-medium text-sub border border-line bg-surface px-3 py-1.5 rounded-full mb-7 mono">
            <span className="w-1.5 h-1.5 rounded-full bg-ok"></span>
            DPDP Act, 2023 · Section 6 · Data Fiduciary Compliant
          </div>

          <h1 className="text-[42px] sm:text-[54px] leading-[1.07] font-extrabold tracking-tight text-ink">
            Your users' consent.<br />
            <span className="text-blue">
              Stored, versioned,<br />
              certified.
            </span>
          </h1>

          <p className="mt-6 text-[16.5px] leading-[1.75] text-sub max-w-[460px]">
            One API call captures the consent record, the notice version hash, the exact timestamp, and the user's session context. No additional logging required.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <a href="#pricing" className="btn-p">
              Get Access{' '}
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
            <a href="#demo" className="btn-g">
              <svg className="w-4 h-4 text-sub" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>{' '}
              Live Demo
            </a>
          </div>

          <div className="mt-9 inline-flex items-center gap-3 bg-[#0B0F17] rounded-xl px-4 py-2.5">
            <span className="mono text-[11.5px] text-[#86EFAC] font-medium">POST</span>
            <span className="mono text-[11.5px] text-[#94A3B8]">/v1/consent/record</span>
            <span className="mono text-[10px] text-[#4B5563] border border-[#1F2937] px-1.5 py-0.5 rounded">
              200 OK · 38ms
            </span>
          </div>
        </div>

        {/* Right Column — Interactive Preview Window */}
        <div className="relative select-none">
          {/* Floating Badges */}
          <div className="hidden sm:flex absolute -left-5 top-8 z-20 bg-card border border-line rounded-[14px] shadow-cardhover px-3.5 py-2.5 items-center gap-2.5 text-[12px] font-medium text-ink">
            <span className="live-dot w-2 h-2 rounded-full bg-ok shrink-0"></span> API Connected
          </div>

          <div className="hidden sm:flex absolute -right-4 top-[38%] z-20 bg-card border border-line rounded-[14px] shadow-cardhover px-3.5 py-2.5 items-center gap-2.5 text-[12px] font-medium text-ink">
            <svg className="w-3.5 h-3.5 text-emerald shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Consent Recorded
          </div>

          <div className="hidden sm:flex absolute left-8 -bottom-4 z-20 bg-card border border-line rounded-[14px] shadow-cardhover px-3.5 py-2.5 items-center gap-2.5 text-[12px] font-medium text-ink">
            <svg className="w-3.5 h-3.5 text-blue shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Audit Verified
          </div>

          {/* Main Card Widget Container */}
          <div className="rounded-xl3 border border-line bg-surface shadow-cardhover p-3">
            <div className="rounded-xl2 bg-card border border-line overflow-hidden">
              {/* Window Header */}
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-line">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]"></span>
                <span className="ml-3 mono text-[11px] text-muted">app.consentry.io/dashboard</span>
                <span className="ml-auto flex items-center gap-1.5 mono text-[10.5px] text-ok font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-ok"></span>live
                </span>
              </div>

              {/* Grid content */}
              <div className="grid grid-cols-3">
                {/* Left Mini Sidebar */}
                <div className="col-span-1 border-r border-line p-3 space-y-1">
                  <button
                    type="button"
                    onClick={() => setActiveTab('overview')}
                    className={`htab w-full rounded-lg px-3 py-2 text-[12px] font-medium flex items-center gap-2 cursor-pointer ${
                      activeTab === 'overview' ? 'active' : 'text-muted'
                    }`}
                  >
                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                    Overview
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveTab('apps')}
                    className={`htab w-full rounded-lg px-3 py-2 text-[12px] font-medium flex items-center gap-2 cursor-pointer ${
                      activeTab === 'apps' ? 'active' : 'text-muted'
                    }`}
                  >
                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    Apps
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveTab('notices')}
                    className={`htab w-full rounded-lg px-3 py-2 text-[12px] font-medium flex items-center gap-2 cursor-pointer ${
                      activeTab === 'notices' ? 'active' : 'text-muted'
                    }`}
                  >
                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Notices
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveTab('certs')}
                    className={`htab w-full rounded-lg px-3 py-2 text-[12px] font-medium flex items-center gap-2 cursor-pointer ${
                      activeTab === 'certs' ? 'active' : 'text-muted'
                    }`}
                  >
                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    Certs
                  </button>
                </div>

                {/* Right Tab Display */}
                <div className="col-span-2 p-3">
                  {activeTab === 'overview' && (
                    <div className="space-y-2.5 animate-log-slide">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="border border-line rounded-lg p-2.5 bg-surface">
                          <div className="text-[10px] text-muted mb-0.5">Consents today</div>
                          <div className="text-[17px] font-bold mono text-ink">18,420</div>
                        </div>
                        <div className="border border-line rounded-lg p-2.5 bg-surface">
                          <div className="text-[10px] text-muted mb-0.5">Opt-in rate</div>
                          <div className="text-[17px] font-bold mono text-ink">94%</div>
                        </div>
                      </div>
                      <div className="border border-line rounded-lg p-2.5 bg-surface">
                        <div className="text-[10px] text-muted mb-2 flex justify-between">
                          <span>Last 7 days</span>
                          <span className="text-ok font-medium">↑ 12.4%</span>
                        </div>
                        <div className="h-12 flex items-end gap-1">
                          <div className="w-full bg-blue-100 rounded-sm" style={{ height: '38%' }}></div>
                          <div className="w-full bg-blue-100 rounded-sm" style={{ height: '52%' }}></div>
                          <div className="w-full bg-blue-100 rounded-sm" style={{ height: '44%' }}></div>
                          <div className="w-full bg-blue-100 rounded-sm" style={{ height: '70%' }}></div>
                          <div className="w-full bg-blue rounded-sm" style={{ height: '88%' }}></div>
                          <div className="w-full bg-blue-100 rounded-sm" style={{ height: '62%' }}></div>
                          <div className="w-full bg-blue-100 rounded-sm" style={{ height: '76%' }}></div>
                        </div>
                        <div className="flex justify-between mt-1.5 mono text-[9px] text-muted">
                          <span>M</span>
                          <span>T</span>
                          <span>W</span>
                          <span>T</span>
                          <span>F</span>
                          <span>S</span>
                          <span>S</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'apps' && (
                    <div className="space-y-1.5 animate-log-slide">
                      <div className="border border-line rounded-lg px-3 py-2 flex justify-between text-[11.5px] bg-surface">
                        <span className="font-medium text-ink">FinTech Consumer App</span>
                        <span className="text-ok font-medium mono">Live</span>
                      </div>
                      <div className="border border-line rounded-lg px-3 py-2 flex justify-between text-[11.5px] bg-surface">
                        <span className="font-medium text-ink">Patient Portal — Web</span>
                        <span className="text-ok font-medium mono">Live</span>
                      </div>
                      <div className="border border-line rounded-lg px-3 py-2 flex justify-between text-[11.5px] bg-surface">
                        <span className="font-medium text-ink">Admin Dashboard</span>
                        <span className="text-warn font-medium mono">Staging</span>
                      </div>
                    </div>
                  )}

                  {activeTab === 'notices' && (
                    <div className="space-y-1.5 animate-log-slide">
                      <div className="border border-line rounded-lg px-3 py-2 bg-surface">
                        <div className="text-[11.5px] font-medium text-ink">Privacy Notice v4.2</div>
                        <div className="mono text-[10px] text-muted">sha:a3f9c1b2 · EN/HI/MR</div>
                      </div>
                      <div className="border border-line rounded-lg px-3 py-2 bg-surface">
                        <div className="text-[11.5px] font-medium text-ink">Marketing Consent v2.0</div>
                        <div className="mono text-[10px] text-muted">sha:b7e2049c · EN/HI</div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'certs' && (
                    <div className="space-y-1.5 animate-log-slide">
                      <div className="border border-line rounded-lg px-3 py-2 flex justify-between text-[11.5px] bg-surface">
                        <span className="flex items-center gap-1.5 font-medium text-ink">
                          <svg className="w-3 h-3 text-emerald" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Q2 2026
                        </span>
                        <span className="mono text-[10px] text-muted">CERT-2026-0091</span>
                      </div>
                      <div className="border border-line rounded-lg px-3 py-2 flex justify-between text-[11.5px] bg-surface">
                        <span className="flex items-center gap-1.5 font-medium text-ink">
                          <svg className="w-3 h-3 text-emerald" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Q1 2026
                        </span>
                        <span className="mono text-[10px] text-muted">CERT-2026-0042</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
